import { useRef, type ElementType, type ReactNode } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { DUR, EASE, STAGGER, DISTANCE, START, MQ } from '@/lib/motion';

gsap.registerPlugin(ScrollTrigger, useGSAP);

interface StaggerGroupProps {
  children: ReactNode;
  as?: ElementType;
  className?: string;
  stagger?: number;
  y?: number;
  start?: string;
}

/**
 * Reveals direct children in sequence.
 *
 * Targets immediate children rather than a class, so consumers just wrap a grid
 * and every card animates without extra markup.
 */
export function StaggerGroup({
  children,
  as: Tag = 'div',
  className = '',
  stagger = STAGGER.item,
  y = DISTANCE.md,
  start = START,
}: StaggerGroupProps) {
  const ref = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;
      const items = Array.from(el.children) as HTMLElement[];
      if (!items.length) return;

      const mm = gsap.matchMedia();

      mm.add(MQ.motion, () => {
        const isMobile = window.innerWidth < 768;
        gsap.set(items, { opacity: 0, y: isMobile ? y * 0.5 : y });

        // See Reveal: content already on screen at load must animate straight
        // away, or a once-only scroll trigger leaves it permanently hidden.
        const alreadyVisible = el.getBoundingClientRect().top < window.innerHeight * 0.9;

        const tween = {
          opacity: 1,
          y: 0,
          duration: DUR.base,
          ease: EASE.out,
          stagger: isMobile ? stagger * 0.6 : stagger,
        };

        if (alreadyVisible) {
          gsap.to(items, tween);
        } else {
          gsap.to(items, { ...tween, scrollTrigger: { trigger: el, start, once: true } });
        }
      });

      mm.add(MQ.reduced, () => {
        gsap.set(items, { opacity: 1, y: 0 });
      });

      // See SplitText: reverting on StrictMode's dev remount can leave items
      // parked in their hidden state, so clear it explicitly.
      return () => {
        mm.revert();
        gsap.set(items, { opacity: 1, y: 0, clearProps: 'transform' });
      };
    },
    { scope: ref },
  );

  return (
    <Tag ref={ref} className={className}>
      {children}
    </Tag>
  );
}
